// FinancialSummaryMath has no settlement-edit handling (the server pool path
// pre-applies edits the same way in V3Budgets::Base before running it), so
// edits must be applied to the budget inputs before the engine computes totals.
//
// The matching here mirrors the server's V3SettlementEditMatcher (used by
// V3Budgets::Base) — keep the two in sync. "Ticket" and "BudgetLineItem"
// overrides/visibilities don't key off the row's own overridable_type, so the
// server resolves them into match_* fields (match_overridable_type/_id/_name)
// during serialization; we match on those, falling back to the raw fields when
// they're absent. The V2 settlement surface has its own copy of this logic in
// SettlementMath.js#postProcessSectionItems.

const SECTION_LABEL_BY_KEY = {
  ticket_line_items: 'Revenue',
  income_line_items: 'Revenue',
  before_adjusted_deductions_line_items: 'Before Adjusted Deductions',
  expense_line_items: 'Expenses',
  addition_line_items: 'Additions',
  deduction_line_items: 'Deductions',
};

const LINE_ITEM_KEY_BY_SECTION = {
  'Tickets': 'ticket_line_items',
  'Revenue': 'income_line_items',
  'Before Adjusted Deductions': 'before_adjusted_deductions_line_items',
  'Expenses': 'expense_line_items',
  'Additions': 'addition_line_items',
  'Deductions': 'deduction_line_items',
};

// Decimal columns serialize as strings (possibly with commas) in as_json.
const toFloat = (value) => {
  if (typeof value === 'number') return value;
  if (value === null || value === undefined) return NaN;
  return parseFloat(String(value).split(',').join(''));
};

// new_amount/new_quantity are allow_blank on V3SettlementOverride, so they can
// serialize as null. Coerce NaN to 0 when writing into engine inputs, matching
// the server's nil-safe .to_f in V3Budgets::Base. Use toFloat (not safeFloat)
// for the match comparisons below, where NaN !== NaN should fail the match.
const safeFloat = (value) => {
  const result = toFloat(value);
  return Number.isNaN(result) ? 0 : result;
};

// Mirrors V3SettlementEditMatcher::AMOUNT_TOLERANCE — keep the values equal.
// NaN still fails the match, since any comparison against NaN is false.
const AMOUNT_TOLERANCE = 1e-6;

const amountsMatch = (a, b) => Math.abs(toFloat(a) - toFloat(b)) < AMOUNT_TOLERANCE;

// Mirrors V3SettlementEditMatcher#matches?: a "BudgetLineItem"-resolved target
// (matchName set) matches a blank-typed row by name; everything else matches on
// overridable_type/overridable_id. Both match fields null means the referenced
// record is gone — matches nothing, same as the server.
const settlementRowMatches = (row, matchType, matchId, matchName) => {
  if (matchName !== null && matchName !== undefined) {
    return !row.overridable_type && row.name === matchName;
  }
  if (matchType !== null && matchType !== undefined) {
    return row.overridable_type === matchType && row.overridable_id === matchId;
  }
  return false;
};

// Resolved match fields are stamped server-side (settlement_edit_json); fall
// back to the raw override/visibility fields when they're absent so a payload
// serialized the old way still matches generic rows.
const matchTarget = (edit, rawType, rawId) => ({
  type: edit.match_overridable_type !== undefined ? edit.match_overridable_type : rawType,
  id: edit.match_overridable_id !== undefined ? edit.match_overridable_id : rawId,
  name: edit.match_name !== undefined ? edit.match_name : null,
});

export const applySettlementEdits = (budget) => {
  const overrides = budget.settlement_overrides || [];
  const visibilities = budget.settlement_visibilities || [];
  const lineItems = budget.settlement_line_items || [];

  const result = { ...budget };

  Object.keys(SECTION_LABEL_BY_KEY).forEach((key) => {
    result[key] = (budget[key] || []).map((item) => ({ ...item }));
  });

  lineItems.forEach((lineItem) => {
    const key = LINE_ITEM_KEY_BY_SECTION[lineItem.section];
    if (!key) return;

    result[key] = result[key].concat([{
      ...lineItem,
      left_hand: lineItem.amount,
      right_hand: lineItem.quantity,
      operator: '*',
      calc_type: 'fixed',
      show_in_offer: true,
      left_hand_forecast: '0',
      right_hand_forecast: '0',
      overridable_id: lineItem.id,
      overridable_type: 'SettlementLineItem',
    }]);
  });

  Object.keys(SECTION_LABEL_BY_KEY).forEach((key) => {
    const section = SECTION_LABEL_BY_KEY[key];

    result[key] = result[key].map((item) => {
      const next = { ...item, section: section };

      const override = overrides.find((o) => {
        if (o.section !== section) return false;
        if (!amountsMatch(o.amount, next.left_hand)) return false;
        const m = matchTarget(o, o.overridable_type, o.overridable_id);
        return settlementRowMatches(next, m.type, m.id, m.name);
      });

      if (override) {
        if (override.new_name) {
          next.name = override.new_name;
        }
        next.left_hand = safeFloat(override.new_amount);
        // Also stamp the actual columns (kept in sync with V3Budgets::Base): pool
        // settlements settle off the Actual variant, not left/right_hand.
        next.left_hand_actual = safeFloat(override.new_amount);
        if (override.new_quantity !== null && override.new_quantity !== undefined) {
          next.right_hand = safeFloat(override.new_quantity);
          next.right_hand_actual = safeFloat(override.new_quantity);
          if (next.calc_type) {
            next.calc_type = 'fixed';
          }
        }
        next.settlementOverride = override;
      }

      // Visibilities created against an overridden row store the override's
      // original amount, not the row's current left_hand. Match against `item`
      // (the pre-override row) so a "BudgetLineItem" name match sees the
      // original name, mirroring the server applying visibilities first.
      const originalAmount = override ? override.amount : item.left_hand;
      const visibility = visibilities.find((v) => {
        if (v.section !== section) return false;
        if (!amountsMatch(v.amount, originalAmount)) return false;
        const m = matchTarget(v, v.settleable_type, v.settleable_id);
        return settlementRowMatches(item, m.type, m.id, m.name);
      });

      if (visibility) {
        next.show_in_offer = visibility.visible;
        next.settlementVisibility = visibility;
      }

      return next;
    });
  });

  return result;
};
